import React, { useState, useEffect } from 'react';
import { 
  Building2, 
  MapPin, 
  Phone, 
  Mail, 
  Globe, 
  Clock,
  Camera,
  CheckCircle2
} from 'lucide-react';
import { useNotifications } from '../context/NotificationContext';
import { supabase } from '../supabaseClient';

const GeneralSettings = () => {
  const { addNotification } = useNotifications();
  const activeUser = JSON.parse(localStorage.getItem('gdt_active_user') || '{}');
  
  const defaultSettings = {
    name: 'GDT - Gestión Médica Premium',
    address: 'Av. Libertador 1234, CABA',
    phone: '+54 11 4567-8900',
    email: 'contacto@gdt-clinica.com',
    website: 'https://gdt-clinica.com',
    timezone: 'GMT-3 (Buenos Aires)',
    language: 'Español',
    logo: null,
    global_holidays: []
  };

  const [clinicData, setClinicData] = useState(defaultSettings);
  const [newHoliday, setNewHoliday] = useState('');
  const [loadingSettings, setLoadingSettings] = useState(true);

  useEffect(() => {
    const fetchAllSettings = async () => {
      try {
        const { data } = await supabase
          .from('system_settings')
          .select('key, value');

        if (data && data.length > 0) {
          const settingsMap = {};
          data.forEach(row => {
            try {
              settingsMap[row.key] = JSON.parse(row.value);
            } catch {
              settingsMap[row.key] = row.value;
            }
          });
          setClinicData(prev => ({
            ...prev,
            ...(settingsMap.clinic_info || {}),
            global_holidays: settingsMap.global_holidays || []
          }));
        }
      } catch (err) {
        console.error('Error loading general settings:', err);
        // Fallback a localStorage si Supabase falla
        const saved = localStorage.getItem('gdt_clinic_settings');
        if (saved) {
          try { setClinicData(JSON.parse(saved)); } catch {}
        }
      } finally {
        setLoadingSettings(false);
      }
    };
    fetchAllSettings();
  }, []);;

  const handleSave = async () => {
    try {
      // Guardamos info clínica general en Supabase
      const clinicInfo = {
        name: clinicData.name,
        address: clinicData.address,
        phone: clinicData.phone,
        email: clinicData.email,
        website: clinicData.website,
        timezone: clinicData.timezone,
        language: clinicData.language,
        logo: clinicData.logo
      };
      await supabase.from('system_settings').upsert([
        { key: 'clinic_info', value: JSON.stringify(clinicInfo) },
        { key: 'global_holidays', value: JSON.stringify(clinicData.global_holidays || []) }
      ]);
      // Sincronizamos también localStorage como caché rápido
      localStorage.setItem('gdt_clinic_settings', JSON.stringify(clinicData));
      addNotification('Configuración general guardada correctamente', 'success');
    } catch (err) {
      addNotification('Error al guardar configuración: ' + err.message, 'error');
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setClinicData({ ...clinicData, logo: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAddHoliday = () => {
    if (newHoliday && !(clinicData.global_holidays || []).includes(newHoliday)) {
      setClinicData({ 
        ...clinicData, 
        global_holidays: [...(clinicData.global_holidays || []), newHoliday] 
      });
      setNewHoliday('');
    }
  };

  const handleRemoveHoliday = (date) => {
    setClinicData({ 
      ...clinicData, 
      global_holidays: clinicData.global_holidays.filter(d => d !== date) 
    });
  };

  if (activeUser.role === 'secretaria') {
    return (
      <div className="dashboard-content" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--text-light)' }}>
        <CheckCircle2 size={48} style={{ color: 'var(--accent)', marginBottom: '1rem', opacity: 0.5 }} />
        <h2>Acceso Restringido</h2>
        <p>No tienes permisos para acceder a la configuración general del sistema.</p>
      </div>
    );
  }

  if (loadingSettings) {
    return (
      <div className="dashboard-content" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--text-light)' }}>
        Cargando configuración...
      </div>
    );
  }

  return (
    <div className="settings-section animate-fade">
      <div className="flex-between">
        <div>
          <h3>Configuraciones Generales</h3>
          <p className="section-desc">Gestioná la identidad institucional, datos de contacto globales y feriados.</p>
        </div>
      </div>

      <div className="clinic-identity-card card glass-panel-premium" style={{ padding: '2rem', marginBottom: '2rem' }}>
        <div style={{ display: 'flex', gap: '2rem', alignItems: 'flex-start' }}>
          <div className="logo-upload-section">
            <div className="logo-preview-box" style={{ width: '120px', height: '120px', borderRadius: '15px', background: 'rgba(255,255,255,0.03)', border: '1px dashed rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', position: 'relative', overflow: 'hidden' }}>
              {clinicData.logo ? (
                <img src={clinicData.logo} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <Building2 size={40} style={{ opacity: 0.3 }} />
              )}
              <div className="upload-overlay" style={{ position: 'absolute', bottom: '0', right: '0', background: 'var(--accent)', padding: '5px', borderRadius: '50%', zIndex: 2 }}>
                <Camera size={14} />
              </div>
              <input 
                type="file" 
                accept="image/png, image/jpeg" 
                onChange={handleLogoUpload}
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer', zIndex: 3 }}
              />
            </div>
            <p style={{ fontSize: '0.7rem', marginTop: '0.5rem', textAlign: 'center' }}>PNG/JPG (200x200)</p>
          </div>

          <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
            <div className="form-group">
              <label>Nombre de la Institución</label>
              <input 
                type="text" 
                value={clinicData.name} 
                onChange={(e) => setClinicData({...clinicData, name: e.target.value})}
              />
            </div>
            <div className="form-group">
              <label>Sitio Web</label>
              <div style={{ position: 'relative' }}>
                <input 
                  type="text" 
                  value={clinicData.website} 
                  onChange={(e) => setClinicData({...clinicData, website: e.target.value})}
                  style={{ paddingLeft: '2.5rem' }}
                />
                <Globe size={16} style={{ position: 'absolute', left: '12px', top: '12px', opacity: 0.5 }} />
              </div>
            </div>
            <div className="form-group">
              <label>Email de Contacto Público</label>
              <div style={{ position: 'relative' }}>
                <input 
                  type="email" 
                  value={clinicData.email} 
                  onChange={(e) => setClinicData({...clinicData, email: e.target.value})}
                  style={{ paddingLeft: '2.5rem' }}
                />
                <Mail size={16} style={{ position: 'absolute', left: '12px', top: '12px', opacity: 0.5 }} />
              </div>
            </div>
            <div className="form-group">
              <label>Teléfono / Central</label>
              <div style={{ position: 'relative' }}>
                <input 
                  type="text" 
                  value={clinicData.phone} 
                  onChange={(e) => setClinicData({...clinicData, phone: e.target.value})}
                  style={{ paddingLeft: '2.5rem' }}
                />
                <Phone size={16} style={{ position: 'absolute', left: '12px', top: '12px', opacity: 0.5 }} />
              </div>
            </div>
          </div>
        </div>

        <div style={{ marginTop: '2rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
          <div className="form-group">
            <label>Dirección Física</label>
            <div style={{ position: 'relative' }}>
              <input 
                type="text" 
                value={clinicData.address} 
                onChange={(e) => setClinicData({...clinicData, address: e.target.value})}
                style={{ paddingLeft: '2.5rem' }}
              />
              <MapPin size={16} style={{ position: 'absolute', left: '12px', top: '12px', opacity: 0.5 }} />
            </div>
          </div>
          <div className="form-group">
            <label>Huso Horario</label>
            <div style={{ position: 'relative' }}>
              <select 
                value={clinicData.timezone} 
                onChange={(e) => setClinicData({...clinicData, timezone: e.target.value})}
                style={{ paddingLeft: '2.5rem' }}
              >
                <option>GMT-3 (Buenos Aires)</option>
                <option>GMT-4 (Santiago)</option>
                <option>GMT-5 (Lima/Bogotá)</option>
              </select>
              <Clock size={16} style={{ position: 'absolute', left: '12px', top: '12px', opacity: 0.5, zIndex: 1 }} />
            </div>
          </div>
        </div>

        <div style={{ marginTop: '2rem', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '2rem' }}>
          <h4>Feriados y Días No Laborables Globales</h4>
          <p style={{ fontSize: '0.8rem', marginBottom: '1rem' }}>
            Agrega fechas donde la institución permanecerá cerrada. El portal de pacientes no ofrecerá turnos en estos días.
          </p>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginBottom: '1rem' }}>
            <input 
              type="date" 
              value={newHoliday}
              onChange={(e) => setNewHoliday(e.target.value)}
              style={{ width: '200px' }}
            />
            <button className="btn-primary" onClick={handleAddHoliday}>AGREGAR FERIADO</button>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {(clinicData.global_holidays || []).map(holiday => (
              <div key={holiday} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'rgba(255,255,255,0.05)', padding: '0.5rem 1rem', borderRadius: '20px' }}>
                <span>{holiday}</span>
                <span onClick={() => handleRemoveHoliday(holiday)} style={{ cursor: 'pointer', color: 'var(--danger)' }}>&times;</span>
              </div>
            ))}
            {(clinicData.global_holidays || []).length === 0 && (
              <span style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.3)' }}>No hay feriados configurados.</span>
            )}
          </div>
        </div>

        <div style={{ marginTop: '2rem', display: 'flex', justifyContent: 'flex-end' }}>
          <button className="btn-primary" onClick={handleSave} style={{ width: '200px' }}>
            GUARDAR CAMBIOS
          </button>
        </div>
      </div>

      <div className="card glass-panel" style={{ padding: '1.5rem', display: 'flex', alignItems: 'center', gap: '1rem', border: '1px solid rgba(0, 229, 255, 0.1)' }}>
        <div style={{ color: '#00e5ff' }}><CheckCircle2 size={24} /></div>
        <div>
          <h4 style={{ margin: 0, fontSize: '0.9rem' }}>Estado del Sistema</h4>
          <p style={{ margin: 0, fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)' }}>Todos los servicios institucionales están sincronizados correctamente con Supabase.</p>
        </div>
      </div>
    </div>
  );
};

export default GeneralSettings;
